import { useMutation, useQueryClient } from '@tanstack/react-query';
import SharedDialog from '../../../../shared/shared-dialog';
import { useState } from 'react';
import { CalendarEventModel, ResourceCalendar } from '../../../../../types';
import {
  Controller,
  SubmitHandler,
  useFieldArray,
  useForm,
} from 'react-hook-form';
import {
  UserKeys,
  updateResourceCalendarEvent,
} from '../../../../api/users-api';
import { handleErrors } from '../../../../utils/errors';
import ChangeLogDialog from '../../../../shared/change-log-dialog';
import { useStore } from '../../../state/zustand-store';
import { useAnalytics } from '../../../../shared/analytics/create-events';
import * as analytics from '../../../../shared/analytics/analytics.json';
import { dayjsWithExtensions } from '../../../../utils/dayjs-config';
import {
  formatEventStartPayload,
  formatEventEndPayload,
} from '../../../../utils/calendar-events-utils';
import { DatePicker, DateTimePicker } from '@mui/x-date-pickers';
import SharedAutocomplete from '../../../../shared/shared-autocomplete';
import AvatarChip from '../../../../shared/avatar-chip';
import { calendarColors } from '../../../../utils/calendar-colors';
import { ModuleList } from '../../../../shared/analytics/module-list';
import {
  CalendarRoles,
  isAbleToSeeUIElement,
} from '../../../../utils/roles/client-roles';

import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';

interface ResourceCalendarEditEventDialogProps {
  open: boolean;
  handleClose: () => void;
  userEmail: string;
  changeLogRoles: string[];
  selectedCalendar: ResourceCalendar;
  selectedEvent: CalendarEventModel;
  dateRange: {
    start: number;
    end: number;
  };
}

export default function ResourceCalendarEditEventDialog({
  open,
  handleClose,
  userEmail,
  changeLogRoles,
  selectedCalendar,
  selectedEvent,
  dateRange,
}: ResourceCalendarEditEventDialogProps) {
  const { createEvent } = useAnalytics();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');

  const [autoCompleteValue, setAutoCompleteValue] = useState('');
  const queryClient = useQueryClient();
  const { setSnackbar } = useStore();

  const [notifyUsers, setNotifyUsers] = useState(false);

  const { mutate, isPending: isCreating } = useMutation({
    mutationFn: (data: Partial<CalendarEventModel>) => {
      const updatedData = {
        ...data,
        start: formatEventStartPayload(data.start),
        end: formatEventEndPayload(data.start, data.end),
        summary: data.summary || '',
      };

      return updateResourceCalendarEvent(
        selectedCalendar.resourceEmail,
        selectedEvent.id,
        notifyUsers,
        updatedData,
        changeLogMessage
      );
    },
    onSuccess: (data) => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: 'Calendar event updated successfully!',
      });

      queryClient.setQueriesData(
        {
          queryKey: [
            UserKeys.CalendarEvents,
            selectedCalendar.resourceEmail,
            userEmail,
            { start: dateRange.start, end: dateRange.end },
          ],
        },

        (oldData: CalendarEventModel[] | undefined) => {
          if (oldData) {
            return oldData.map((event) => {
              if (event.id === selectedEvent.id) {
                return data;
              }
              return event;
            });
          } else {
            return [];
          }
        }
      );
      setChangeLogMessage('');
      setNotifyUsers(false);
      setAutoCompleteValue('');
      reset();
      handleClose();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage:
          'There was an issue updating this calendar event. Please refresh and try again.',
      });
    },
    onSettled: () => {
      createEvent(
        analytics.calendars.resource_calendars.update_resource_calendar_event,
        {
          module: ModuleList.modules.calendars.name,
          section: ModuleList.modules.calendars.sections.resourceCalendars,
        }
      );
      setOpenChangeLogDialog(false);
    },
  });

  const {
    control,
    handleSubmit,
    reset,
    getValues,
    setValue,
    watch,
    formState: { isLoading, isDirty },
  } = useForm<Partial<CalendarEventModel>>({
    defaultValues: {
      ...selectedEvent,
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'attendees',
  });

  const onSubmit: SubmitHandler<Partial<CalendarEventModel>> = (data) => {
    // Validate that end date is not before start date
    if (data.start?.dateTime && data.end?.dateTime) {
      const startDate = dayjsWithExtensions(data.start.dateTime);
      const endDate = dayjsWithExtensions(data.end.dateTime);
      if (endDate.isBefore(startDate)) {
        setSnackbar({
          open: true,
          severity: 'error',
          message: 'End date must be on or after the start date.',
        });
        return;
      }
    }

    if (
      isAbleToSeeUIElement(
        changeLogRoles,
        CalendarRoles.EditResourceEvents.value
      )
    ) {
      setOpenChangeLogDialog(true);
    } else {
      mutate(data);
    }
  };

  const allDay = watch('start.allDay');
  const startDateTime = watch('start.dateTime');

  return (
    <SharedDialog
      open={open}
      handleClose={() => {
        handleClose();
        setChangeLogMessage('');
        setNotifyUsers(false);
        setAutoCompleteValue('');
        reset();
      }}
      title="Update Event"
      dialogContent={
        <Stack gap={1.5} justifyContent="flex-start">
          <Stack flexDirection="row" gap={1}>
            <Controller
              name="summary"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  autoFocus
                  label="Title"
                  autoComplete="off"
                  sx={{ flexGrow: 1 }}
                />
              )}
            />
            <Controller
              name="colorId"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  inputProps={{
                    id: 'colorId',
                  }}
                  InputLabelProps={{
                    htmlFor: 'colorId',
                  }}
                  select
                  SelectProps={{
                    MenuProps: {
                      PaperProps: {
                        style: {
                          maxHeight: 200,
                        },
                      },
                    },
                  }}
                  label="Color"
                  sx={{ width: 80 }}
                >
                  {Object.keys(calendarColors.event).map((colorId) => (
                    <MenuItem key={colorId} value={colorId}>
                      <Box
                        sx={{
                          backgroundColor:
                            calendarColors.event[
                              colorId as keyof typeof calendarColors.event
                            ].background,
                          width: 20,
                          height: 20,
                          mx: 'auto',
                        }}
                      ></Box>
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
          </Stack>

          <Box>
            <Stack flexDirection="row" justifyContent="space-between">
              <Controller
                name="start.dateTime"
                control={control}
                render={({ field: { value, onChange } }) => {
                  if (allDay) {
                    return (
                      <DatePicker
                        value={dayjsWithExtensions(value)}
                        onChange={(newValue) => {
                          if (newValue) {
                            onChange(newValue.valueOf());
                            setValue('end.dateTime', newValue.valueOf(), {
                              shouldDirty: true,
                            });
                          }
                        }}
                        label="From"
                        sx={{ minWidth: 220 }}
                      />
                    );
                  } else {
                    return (
                      <DateTimePicker
                        value={dayjsWithExtensions(value)}
                        onChange={(newValue) => {
                          if (newValue) {
                            onChange(newValue.valueOf());
                            setValue(
                              'end.dateTime',
                              newValue.add(30, 'minutes').valueOf(),
                              {
                                shouldDirty: true,
                              }
                            );
                          }
                        }}
                        label="From"
                        sx={{ minWidth: 220 }}
                      />
                    );
                  }
                }}
              />
              <Controller
                name="end.dateTime"
                control={control}
                rules={{
                  validate: (value) => {
                    if (!value || !startDateTime) return true;
                    const endDate = dayjsWithExtensions(value);
                    const startDate = dayjsWithExtensions(startDateTime);
                    return (
                      !endDate.isBefore(startDate) ||
                      'End date must be on or after the start date'
                    );
                  },
                }}
                render={({
                  field: { value, onChange },
                  fieldState: { error },
                }) => {
                  if (allDay) {
                    return (
                      <DatePicker
                        value={dayjsWithExtensions(value)}
                        onChange={(newValue) => {
                          if (newValue) {
                            onChange(newValue.valueOf());
                          }
                        }}
                        label="To"
                        sx={{ minWidth: 220 }}
                        minDate={
                          startDateTime != null
                            ? dayjsWithExtensions(startDateTime)
                            : undefined
                        }
                        slotProps={{
                          textField: {
                            error: !!error,
                            helperText: error?.message || ' ',
                          },
                        }}
                      />
                    );
                  }
                  return (
                    <DateTimePicker
                      value={dayjsWithExtensions(value)}
                      onChange={(newValue) => {
                        if (newValue) {
                          onChange(newValue.valueOf());
                        }
                      }}
                      label="To"
                      sx={{ minWidth: 220 }}
                      minDateTime={
                        startDateTime != null
                          ? dayjsWithExtensions(startDateTime)
                          : undefined
                      }
                      slotProps={{
                        textField: {
                          error: !!error,
                          helperText: error?.message || ' ',
                        },
                      }}
                    />
                  );
                }}
              />
            </Stack>
            <Controller
              name="start.allDay"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={field.value}
                      onChange={field.onChange}
                      name="calendarEvents"
                    />
                  }
                  label="All Day"
                />
              )}
            />
          </Box>
          <Stack flexDirection="row" gap={1}>
            <Box sx={{ flexGrow: 1 }}>
              <SharedAutocomplete
                sources={['users', 'groups']}
                autocompleteValue={autoCompleteValue}
                setAutoCompleteValue={setAutoCompleteValue}
                onSelection={(value) => {
                  if (!value) return;
                  append({
                    email: value,
                  });
                  setAutoCompleteValue('');
                }}
              />
            </Box>
          </Stack>
          <Stack
            flexDirection="row"
            sx={{
              maxHeight: 80,
              overflowY: 'auto',
              flexWrap: 'wrap',
            }}
            gap={0.5}
          >
            {fields.map((field, index) => {
              return (
                <AvatarChip
                  key={field.email}
                  email={field.email}
                  type={'User'}
                  onDelete={() => remove(index)}
                />
              );
            })}
          </Stack>

          <Controller
            name="location"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Location"
                autoComplete="off"
                sx={{ minWidth: 250 }}
              />
            )}
          />
          <Controller
            name="description"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Description"
                autoComplete="off"
                sx={{ minWidth: 250 }}
                multiline
                rows={3}
              />
            )}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={notifyUsers}
                onChange={(e) => setNotifyUsers(e.target.checked)}
              />
            }
            label="Notify guests of changes"
          />
          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={() => {
              mutate(getValues());
            }}
            mutateLoading={isCreating}
          />
        </Stack>
      }
      primaryButton={{
        title: 'Update Event',
        action: handleSubmit(onSubmit),
        loading: isCreating || isLoading,
        disabled: isCreating || isLoading || !isDirty,
      }}
    />
  );
}
